<script setup lang="ts">
    import type { Schemas } from '#shopware';
    import { Splide, SplideSlide } from '@splidejs/vue-splide';
    import '@splidejs/vue-splide/css/core';
    import { useWindowSize } from '@vueuse/core';
    import 'vue-image-zoomer/dist/style.css';
    import defaultImage from '@/assets/images/no-image.jpg';
    import type { ProductItemGroup } from '~/types/censhare/productItemGroup';
    import type { components } from '@shopware/api-client/api-types';

    type Slide = {
        id: string | number;
        media?: components['schemas']['Media'];
        nameLocalized?: Record<string, string>;
        position?: number;
    };

    const props = defineProps<{ product?: Schemas['Product']; fachkreisProduct?: ProductItemGroup }>();
    const { lang } = useLanguage();
    const { t } = useI18n();
    const { isCurrentUserParticipating } = useBonus();
    const { isRoot } = usePlatformMeta();

    const main = ref<InstanceType<typeof Splide>>();
    const thumbs = ref<InstanceType<typeof Splide>>();
    const slideIndex = ref(1);
    const { width } = useWindowSize();

    const mainOptions = {
        pagination: false,
        arrows: false,
        keyboard: true,
        flickPower: 74,
        flickMaxPages: 0.2,
        lazyload: 'nearby',
        preloadPages: 1
    };

    const thumbsOptions = {
        type: 'slide',
        arrows: false,
        pagination: false,
        drag: 'free',
        direction: !props.fachkreisProduct ? 'ttb' : 'ltr',
        keyboard: true,
        height: 'auto',
        focus: 0,
        isNavigation: true,
        breakpoints: { 768: { direction: 'ltr', drag: 'free' } },
        i18n: {
            pageX: t(`product.gallery.option1`, 'Galerie Bild %s'),
            slideX: t(`product.gallery.option1`, 'Galerie Bild %s'),
            slide: t(`product.gallery.option2`, 'Produktbild'),
            carousel: t(`product.gallery.option3`, 'Bilder Galerie')
        }
    };

    const slides = computed<Slide[]>(() => {
        let rawSlides: Slide[] = [];

        if (props.product?.media && Array.isArray(props.product.media)) {
            rawSlides = props.product.media;
        } else if (props.fachkreisProduct?.media) {
            rawSlides = [
                ...(props.fachkreisProduct.media.specialistGroupGallery ??
                    [props.fachkreisProduct.media.mainPicture?.[0]].filter(Boolean))
            ] as Slide[];
        }
        return rawSlides.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    });

    const isFachkreisProduct = computed(() => !!props.fachkreisProduct);
    const isEmpty = computed(() => slides.value.length === 0);

    onMounted(() => {
        const thumbsSplide = thumbs.value?.splide;
        if (thumbsSplide) main.value?.sync(thumbsSplide);
    });

    const overWriteThumbnailControls = (index: number) => main.value?.go(index);
</script>

<template>
    <div
        v-if="slides.length || isEmpty"
        :class="{
            'slider-wrapper': !isEmpty,
            '--fachkreis': fachkreisProduct
        }"
    >
        <div class="main-wrapper">
            <div class="point-badge-wrapper">
                <PointBadge
                    v-if="isCurrentUserParticipating && isRoot && product?.calculatedCheapestPrice?.unitPrice"
                    :points="calculatePointsFromPrice(product.calculatedCheapestPrice.unitPrice)"
                ></PointBadge>
            </div>
            <div
                v-if="slides.length > 1"
                class="image-index"
            >
                {{ slideIndex + '/' + slides.length }}
            </div>

            <Splide
                v-if="!isEmpty"
                ref="main"
                aria-labelledby="Produktbild"
                :options="mainOptions"
                class="main-slider"
                :class="{ '--fachkreis': fachkreisProduct }"
                @splide:move="(_: number, i: number) => (slideIndex = (i % slides.length) + 1)"
            >
                <SplideSlide
                    v-for="slide in slides"
                    :key="slide.id"
                >
                    <!-- Desktop -->
                    <ShopwarePicture
                        v-if="width > 1024 && !isFachkreisProduct && slide.media"
                        :alt="slide.media?.alt"
                        type="mainPicture"
                        :media="slide.media"
                        :eager="true"
                        :zoom="true"
                        class="image"
                        zoom-class="image"
                    />

                    <CenshareImage
                        v-else-if="width > 1024 && isFachkreisProduct"
                        :image-id="Number(slide.id)"
                        :name="slide.media?.alt || (slide.nameLocalized?.[lang] ?? slide.media?.fileName)"
                        :zoom="true"
                        type="productGallery"
                        :eager="true"
                        custom-class="image"
                    />

                    <!-- Mobile -->
                    <CenshareImage
                        v-else-if="isFachkreisProduct && width <= 1024"
                        :image-id="Number(slide.id)"
                        :name="slide.media?.alt || (slide.nameLocalized?.[lang] ?? slide.media?.fileName)"
                        type="productGallery"
                        :eager="true"
                        custom-class="image"
                    />

                    <ShopwarePicture
                        v-else-if="!isFachkreisProduct && width <= 1024 && slide.media"
                        :alt="slide.media?.alt"
                        type="mainPicture"
                        :media="slide.media"
                        :eager="true"
                        class="image"
                    />
                </SplideSlide>
            </Splide>

            <img
                v-if="isEmpty"
                :src="defaultImage"
                alt="no-image"
                class="error"
            />
        </div>

        <div
            v-if="slides.length > 1"
            class="thumb-wrapper"
        >
            <Splide
                ref="thumbs"
                :aria-label="'Bilder von ' + (product?.name || fachkreisProduct?.name)"
                :options="thumbsOptions"
                class="thumb-slider"
                :class="{ '--fachkreis': fachkreisProduct }"
            >
                <SplideSlide
                    v-for="(slide, index) in slides"
                    :key="slide.id"
                    :class="{ '--is-active': slideIndex === index + 1 }"
                    @click.prevent="overWriteThumbnailControls(index)"
                    @touchstart.prevent="overWriteThumbnailControls(index)"
                >
                    <CenshareImage
                        v-if="isFachkreisProduct"
                        :image-id="Number(slide.id)"
                        :name="slide.media?.alt || (slide.nameLocalized?.[lang] ?? slide.media?.fileName)"
                        type="productThumbnail"
                        :eager="true"
                    />
                    <ShopwarePicture
                        v-else-if="slide.media"
                        :alt="slide.media?.alt"
                        :media="slide.media"
                        class="image"
                        type="thumbnail"
                        :eager="true"
                    />
                </SplideSlide>
            </Splide>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/detail-page/front-end-detail-page-gallery.scss';
</style>
